import type { ReactNode } from "react";
import { Icon } from "@iconify/react";
import type { EmpiConflictDetail, EmpiConflictOption } from "@/shared/types/empi";
import { ComparisonTable } from "@/shared/components/ComparisonTable";
import { conflictComparison, formatEmpiDate, sourceName, trustText } from "./empiPresentation";

/**
 * The values a GOLDEN_RECORD_CONFLICT is about, in the platform's comparison
 * card: one column per option (where it came from, in plain words, never a
 * tier number) with the rows the check is about highlighted. Selectable (a radio
 * group) when onSelect is given — the patient's advisory pick, or the
 * clinician's CHOOSE_VALUE / CORRECT_VALUE target.
 */
export function ConflictOptions({
  detail,
  selectedId,
  onSelect,
  label,
  renderBadge,
}: {
  detail: EmpiConflictDetail;
  selectedId?: string | null;
  onSelect?: (recordId: string) => void;
  /** Accessible name of the group. */
  label: string;
  /** Extra tag on an option (e.g. "Patient's pick"). */
  renderBadge?: (option: EmpiConflictOption) => ReactNode;
}) {
  const { rows, values, differs } = conflictComparison(detail);

  return (
    <ComparisonTable
      label={label}
      rows={rows}
      differs={differs}
      columns={detail.options.map((option, index) => ({
        key: option.recordId,
        testId: "conflict-option",
        ariaLabel: `Option ${index + 1}`,
        selected: selectedId === option.recordId,
        onSelect: onSelect ? () => onSelect(option.recordId) : undefined,
        values: values[index],
        header: (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <p className="!mb-0 text-sm font-extrabold text-[#0A2947]">Option {index + 1}</p>
              {renderBadge?.(option)}
            </div>
            <p className="!mb-0 !mt-1 text-[11px] leading-4 text-[#6B7280]" data-testid="conflict-option-trust">
              {trustText(option)}
            </p>
          </>
        ),
        footer: (
          <>
            <p className="!mb-0 flex min-w-0 items-center gap-1.5">
              <Icon icon="lucide:file-text" className="h-3 w-3 shrink-0" />
              <span className="truncate">{sourceName(option)}</span>
            </p>
            <p className="!mb-0 !mt-0.5">Recorded {formatEmpiDate(option.recordedAt)}</p>
          </>
        ),
      }))}
    />
  );
}
